import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogPanel, Title, Text, Badge, Button } from '@tremor/react';
import { api } from '../../lib/api';
import { useAppEnvironment } from '../../hooks/useAppEnvironment';
import { DiffList } from '../DiffList';
import type {
  RegistryExportPayload,
  RegistryDiff,
  RegistryImportSelections,
  RegistryImportApplyResponse,
  RegistryClassifierDiff,
  RegistryVersionDiff,
  RegistryBundleDiff,
} from '../../types';

type Step = 'upload' | 'review' | 'applying' | 'done';

interface ImportRegistryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// ----------------------------------------------------------------------------
// Default selection logic: pick safe defaults that an admin can override.
// New / mutable-changed records default to apply=true; unchanged and
// immutable-conflict default to apply=false. Pointer changes (currentVersion,
// isDefault) default to off so a routine import doesn't silently flip prod's
// default version or default bundle.
// ----------------------------------------------------------------------------
function buildDefaultSelections(diff: RegistryDiff): RegistryImportSelections {
  const selections: RegistryImportSelections = {
    classifiers: {},
    versions: {},
    bundles: {},
  };
  for (const c of diff.classifiers) {
    selections.classifiers[c.key] = {
      apply: c.status !== 'unchanged',
      // For brand-new classifiers, default to setting currentVersion so the
      // imported classifier is usable. For existing classifiers, require
      // explicit opt-in.
      updateCurrentVersion: c.status === 'new',
    };
  }
  for (const v of diff.versions) {
    selections.versions[v.compositeKey] = {
      apply: v.status === 'new' || v.status === 'mutable-changed',
      overrideImmutable: false,
    };
  }
  for (const b of diff.bundles) {
    selections.bundles[b.key] = {
      apply: b.status !== 'unchanged',
      updateIsDefault: false,
    };
  }
  return selections;
}

export function ImportRegistryModal({ isOpen, onClose, onSuccess }: ImportRegistryModalProps) {
  const env = useAppEnvironment();
  const [step, setStep] = useState<Step>('upload');
  const [payload, setPayload] = useState<RegistryExportPayload | null>(null);
  const [diff, setDiff] = useState<RegistryDiff | null>(null);
  const [selections, setSelections] = useState<RegistryImportSelections | null>(null);
  const [applyResult, setApplyResult] = useState<RegistryImportApplyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setPayload(null);
      setDiff(null);
      setSelections(null);
      setApplyResult(null);
      setError(null);
      setIsUploading(false);
    }
  }, [isOpen]);

  const destinationIsProd = env?.appEnvironment === 'prod' || env?.appEnvironment === 'production';

  const handleFile = async (file: File) => {
    setIsUploading(true);
    setError(null);
    try {
      const text = await file.text();
      let parsed: RegistryExportPayload;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('Selected file is not valid JSON');
      }
      if (parsed.schemaVersion !== 1) {
        throw new Error(`Unsupported schemaVersion: ${parsed.schemaVersion}. Expected 1.`);
      }
      const d = await api.previewRegistryImport(parsed);
      setPayload(parsed);
      setDiff(d);
      setSelections(buildDefaultSelections(d));
      setStep('review');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview import');
    } finally {
      setIsUploading(false);
    }
  };

  const handleApply = async () => {
    if (!payload || !selections) return;
    setStep('applying');
    setError(null);
    try {
      const result = await api.applyRegistryImport(payload, selections);
      setApplyResult(result);
      setStep('done');
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply import');
      setStep('review');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {step === 'upload' && (
          <UploadStep
            isUploading={isUploading}
            error={error}
            onFile={handleFile}
            onClose={onClose}
          />
        )}
        {step === 'review' && diff && selections && (
          <ReviewStep
            diff={diff}
            destinationIsProd={destinationIsProd}
            selections={selections}
            onChange={setSelections}
            error={error}
            onCancel={onClose}
            onApply={handleApply}
          />
        )}
        {step === 'applying' && <ApplyingStep />}
        {step === 'done' && applyResult && (
          <DoneStep result={applyResult} onClose={onClose} />
        )}
      </DialogPanel>
    </Dialog>
  );
}

// ============================================================================
// Step 1: Upload
// ============================================================================

function UploadStep({
  isUploading,
  error,
  onFile,
  onClose,
}: {
  isUploading: boolean;
  error: string | null;
  onFile: (file: File) => void;
  onClose: () => void;
}) {
  return (
    <>
      <Title className="text-white">Import Classifier Registry</Title>
      <Text className="text-gray-400 mt-1">
        Upload a JSON file exported from another environment. You'll be shown a per-record diff before anything is written.
      </Text>

      <div className="mt-6">
        <label className="block border-2 border-dashed border-gray-700 rounded-lg p-8 text-center cursor-pointer hover:border-gray-600 transition-colors">
          <input
            type="file"
            accept=".json,application/json"
            className="hidden"
            disabled={isUploading}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
          />
          <p className="text-gray-300 text-sm">
            {isUploading ? 'Reading and previewing…' : 'Click to choose a .json export file'}
          </p>
          <p className="text-gray-500 text-xs mt-1">
            Generated by the Export button on this page or another environment
          </p>
        </label>
      </div>

      {error && (
        <div className="mt-4 bg-red-950/50 border border-red-900 rounded p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
      </div>
    </>
  );
}

// ============================================================================
// Step 2: Review
// ============================================================================

function statusColor(status: string): 'gray' | 'emerald' | 'blue' | 'red' | 'amber' {
  switch (status) {
    case 'new':
      return 'emerald';
    case 'changed':
    case 'mutable-changed':
      return 'blue';
    case 'immutable-conflict':
      return 'red';
    case 'unchanged':
      return 'gray';
    default:
      return 'amber';
  }
}

function ReviewStep({
  diff,
  destinationIsProd,
  selections,
  onChange,
  error,
  onCancel,
  onApply,
}: {
  diff: RegistryDiff;
  destinationIsProd: boolean;
  selections: RegistryImportSelections;
  onChange: (s: RegistryImportSelections) => void;
  error: string | null;
  onCancel: () => void;
  onApply: () => void;
}) {
  const selectedCount = useMemo(() => {
    let n = 0;
    Object.values(selections.classifiers).forEach(s => s.apply && n++);
    Object.values(selections.versions).forEach(s => s.apply && n++);
    Object.values(selections.bundles).forEach(s => s.apply && n++);
    return n;
  }, [selections]);

  const updateClassifier = (key: string, patch: Partial<RegistryImportSelections['classifiers'][string]>) => {
    onChange({
      ...selections,
      classifiers: {
        ...selections.classifiers,
        [key]: { ...selections.classifiers[key], ...patch },
      },
    });
  };
  const updateVersion = (key: string, patch: Partial<RegistryImportSelections['versions'][string]>) => {
    onChange({
      ...selections,
      versions: {
        ...selections.versions,
        [key]: { ...selections.versions[key], ...patch },
      },
    });
  };
  const updateBundle = (key: string, patch: Partial<RegistryImportSelections['bundles'][string]>) => {
    onChange({
      ...selections,
      bundles: { ...selections.bundles, [key]: { ...selections.bundles[key], ...patch } },
    });
  };

  return (
    <>
      <Title className="text-white">Review changes</Title>

      {/* Source → destination banner */}
      <div
        className={`mt-3 rounded-lg p-3 border ${
          destinationIsProd
            ? 'bg-red-950/40 border-red-900 text-red-200'
            : 'bg-blue-950/40 border-blue-900 text-blue-200'
        }`}
      >
        <p className="text-sm font-medium">
          Importing from <code className="font-mono">{diff.sourceEnv}</code> into{' '}
          <code className="font-mono">{diff.destinationEnv}</code>
          {destinationIsProd && ' — this is production'}
        </p>
        <p className="text-xs opacity-80 mt-1">
          Scope: {diff.scope}
          {diff.rootBundleKeys.length > 0 && ` (${diff.rootBundleKeys.join(', ')})`}
        </p>
      </div>

      {/* Warnings */}
      {diff.warnings.length > 0 && (
        <div className="mt-3 bg-amber-950/40 border border-amber-900 rounded p-3 space-y-1">
          {diff.warnings.map((w, i) => (
            <p key={i} className="text-amber-300 text-xs">
              <span className="font-mono text-amber-500/70">[{w.code}]</span> {w.message}
            </p>
          ))}
        </div>
      )}

      {/* Sections */}
      <div className="mt-4 space-y-4">
        <DiffSection
          title="Classifiers"
          count={diff.classifiers.length}
          rows={diff.classifiers.map(c => (
            <ClassifierRow
              key={c.key}
              c={c}
              sel={selections.classifiers[c.key]}
              onChange={patch => updateClassifier(c.key, patch)}
            />
          ))}
        />
        <DiffSection
          title="Classifier Versions"
          count={diff.versions.length}
          rows={diff.versions.map(v => (
            <VersionRow
              key={v.compositeKey}
              v={v}
              sel={selections.versions[v.compositeKey]}
              onChange={patch => updateVersion(v.compositeKey, patch)}
            />
          ))}
        />
        <DiffSection
          title="Bundles"
          count={diff.bundles.length}
          rows={diff.bundles.map(b => (
            <BundleRow
              key={b.key}
              b={b}
              sel={selections.bundles[b.key]}
              onChange={patch => updateBundle(b.key, patch)}
            />
          ))}
        />
      </div>

      {error && (
        <div className="mt-4 bg-red-950/50 border border-red-900 rounded p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <span className="text-xs text-gray-500">{selectedCount} record(s) selected</span>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color={destinationIsProd ? 'red' : undefined}
            onClick={onApply}
            disabled={selectedCount === 0}
          >
            Apply {selectedCount} change{selectedCount === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </>
  );
}

function DiffSection({
  title,
  count,
  rows,
}: {
  title: string;
  count: number;
  rows: React.ReactNode[];
}) {
  const [expanded, setExpanded] = useState(true);
  if (count === 0) return null;
  return (
    <div className="bg-gray-950/60 border border-gray-800 rounded-lg">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-gray-800/40"
      >
        <span className="text-sm text-gray-300 font-medium">
          {title} <span className="text-gray-500">({count})</span>
        </span>
        <span className="text-gray-500 text-xs">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded && <div className="border-t border-gray-800 divide-y divide-gray-800">{rows}</div>}
    </div>
  );
}

function Row({
  applyChecked,
  onApplyChange,
  applyDisabled,
  title,
  status,
  meta,
  details,
}: {
  applyChecked: boolean;
  onApplyChange: (next: boolean) => void;
  applyDisabled?: boolean;
  title: React.ReactNode;
  status: string;
  meta?: React.ReactNode;
  details?: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="px-4 py-2">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={applyChecked}
          disabled={applyDisabled}
          onChange={e => onApplyChange(e.target.checked)}
          className="mt-1 accent-blue-500"
        />
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left flex items-center gap-2"
          >
            <span className="text-sm text-gray-200 font-mono truncate">{title}</span>
            <Badge color={statusColor(status)} size="xs">
              {status}
            </Badge>
            {meta}
            <span className="ml-auto text-gray-600 text-xs">{expanded ? '▾' : '▸'}</span>
          </button>
          {expanded && details && <div className="mt-2 pl-1">{details}</div>}
        </div>
      </div>
    </div>
  );
}

function ClassifierRow({
  c,
  sel,
  onChange,
}: {
  c: RegistryClassifierDiff;
  sel: RegistryImportSelections['classifiers'][string];
  onChange: (patch: Partial<RegistryImportSelections['classifiers'][string]>) => void;
}) {
  const meta = c.currentVersionChange ? (
    <span className="text-[10px] text-amber-400 font-mono">
      currentVersion: {c.currentVersionChange.from ?? '—'} → {c.currentVersionChange.to ?? '—'}
    </span>
  ) : null;
  return (
    <Row
      applyChecked={sel.apply}
      onApplyChange={v => onChange({ apply: v })}
      title={c.key}
      status={c.status}
      meta={meta}
      details={
        <div className="space-y-2">
          {c.currentVersionChange && (
            <label className="flex items-center gap-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={Boolean(sel.updateCurrentVersion)}
                onChange={e => onChange({ updateCurrentVersion: e.target.checked })}
                disabled={!sel.apply}
                className="accent-amber-500"
              />
              Also update currentVersion pointer ({c.currentVersionChange.from ?? '—'} →{' '}
              {c.currentVersionChange.to ?? '—'})
            </label>
          )}
          <DiffList diffs={c.fieldDiffs} emptyMessage="No metadata changes" />
        </div>
      }
    />
  );
}

function VersionRow({
  v,
  sel,
  onChange,
}: {
  v: RegistryVersionDiff;
  sel: RegistryImportSelections['versions'][string];
  onChange: (patch: Partial<RegistryImportSelections['versions'][string]>) => void;
}) {
  const isImmutableConflict = v.status === 'immutable-conflict';
  const meta =
    v.immutableFieldDiffs.length > 0 ? (
      <span className="text-[10px] text-red-400 font-mono">
        {v.immutableFieldDiffs.length} immutable field(s) differ
      </span>
    ) : null;
  return (
    <Row
      applyChecked={sel.apply}
      onApplyChange={value => onChange({ apply: value })}
      title={v.compositeKey}
      status={v.status}
      meta={meta}
      details={
        <div className="space-y-3">
          {isImmutableConflict && (
            <label className="flex items-start gap-2 text-xs text-red-300">
              <input
                type="checkbox"
                checked={Boolean(sel.overrideImmutable)}
                onChange={e => onChange({ overrideImmutable: e.target.checked })}
                disabled={!sel.apply}
                className="mt-0.5 accent-red-500"
              />
              <span>
                Override immutability and recreate this version (deletes the existing version document on
                target before re-inserting). Only do this if you're certain — versions are intended to be
                immutable snapshots.
              </span>
            </label>
          )}
          {v.immutableFieldDiffs.length > 0 && (
            <div>
              <p className="text-[10px] uppercase text-red-400/80 mb-1">Immutable fields differ</p>
              <DiffList diffs={v.immutableFieldDiffs} />
            </div>
          )}
          {v.mutableFieldDiffs.length > 0 && (
            <div>
              <p className="text-[10px] uppercase text-blue-400/80 mb-1">Mutable fields differ</p>
              <DiffList diffs={v.mutableFieldDiffs} />
            </div>
          )}
          {v.status === 'new' && (
            <p className="text-xs text-gray-500 italic">Brand-new version — will be created.</p>
          )}
        </div>
      }
    />
  );
}

function BundleRow({
  b,
  sel,
  onChange,
}: {
  b: RegistryBundleDiff;
  sel: RegistryImportSelections['bundles'][string];
  onChange: (patch: Partial<RegistryImportSelections['bundles'][string]>) => void;
}) {
  const meta = b.isDefaultChange ? (
    <span className="text-[10px] text-amber-400 font-mono">
      isDefault: {String(b.isDefaultChange.from)} → {String(b.isDefaultChange.to)}
    </span>
  ) : null;
  return (
    <Row
      applyChecked={sel.apply}
      onApplyChange={v => onChange({ apply: v })}
      title={b.key}
      status={b.status}
      meta={meta}
      details={
        <div className="space-y-2">
          {b.isDefaultChange && (
            <label className="flex items-center gap-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={Boolean(sel.updateIsDefault)}
                onChange={e => onChange({ updateIsDefault: e.target.checked })}
                disabled={!sel.apply}
                className="accent-amber-500"
              />
              Also update isDefault flag ({String(b.isDefaultChange.from)} → {String(b.isDefaultChange.to)})
            </label>
          )}
          <DiffList diffs={b.fieldDiffs} emptyMessage="No bundle field changes" />
        </div>
      }
    />
  );
}

// ============================================================================
// Step 3: Applying / Done
// ============================================================================

function ApplyingStep() {
  return (
    <div className="py-12 text-center">
      <p className="text-gray-300 animate-pulse">Applying selected changes…</p>
    </div>
  );
}

function DoneStep({
  result,
  onClose,
}: {
  result: RegistryImportApplyResponse;
  onClose: () => void;
}) {
  const allOutcomes = [...result.outcomes.versions, ...result.outcomes.classifiers, ...result.outcomes.bundles];
  const errors = allOutcomes.filter(o => o.action === 'error');
  return (
    <>
      <Title className="text-white">Import complete</Title>
      <Text className="text-gray-400 mt-1">
        Imported from <code className="font-mono">{result.sourceEnv}</code> into{' '}
        <code className="font-mono">{result.destinationEnv}</code>.
      </Text>

      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <SummaryBox
          label="Versions"
          created={result.counts.versionsCreated}
          updated={result.counts.versionsUpdated}
          recreated={result.counts.versionsRecreated}
          skipped={result.counts.versionsSkipped}
          errored={result.counts.versionsErrored}
        />
        <SummaryBox
          label="Classifiers"
          created={result.counts.classifiersCreated}
          updated={result.counts.classifiersUpdated}
          skipped={result.counts.classifiersSkipped}
          errored={result.counts.classifiersErrored}
        />
        <SummaryBox
          label="Bundles"
          created={result.counts.bundlesCreated}
          updated={result.counts.bundlesUpdated}
          skipped={result.counts.bundlesSkipped}
          errored={result.counts.bundlesErrored}
        />
      </div>

      {errors.length > 0 && (
        <div className="mt-4 bg-red-950/40 border border-red-900 rounded p-3 space-y-1">
          <p className="text-red-300 text-sm font-medium">{errors.length} error(s)</p>
          {errors.map((e, i) => (
            <p key={i} className="text-red-400 text-xs font-mono">
              {e.key}: {e.error}
            </p>
          ))}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button onClick={onClose}>Done</Button>
      </div>
    </>
  );
}

function SummaryBox({
  label,
  created = 0,
  updated = 0,
  recreated = 0,
  skipped = 0,
  errored = 0,
}: {
  label: string;
  created?: number;
  updated?: number;
  recreated?: number;
  skipped?: number;
  errored?: number;
}) {
  return (
    <div className="bg-gray-950/60 border border-gray-800 rounded p-3">
      <p className="text-gray-400 text-xs uppercase tracking-wide">{label}</p>
      <div className="mt-1 space-y-0.5 text-xs">
        {created > 0 && <p className="text-emerald-400">created: {created}</p>}
        {updated > 0 && <p className="text-blue-400">updated: {updated}</p>}
        {recreated > 0 && <p className="text-amber-400">recreated: {recreated}</p>}
        {skipped > 0 && <p className="text-gray-500">skipped: {skipped}</p>}
        {errored > 0 && <p className="text-red-400">errors: {errored}</p>}
        {created + updated + recreated + skipped + errored === 0 && (
          <p className="text-gray-600">—</p>
        )}
      </div>
    </div>
  );
}
